import { useState } from "react";
import { LockKeyhole, LogIn, KeyRound, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../auth";
import { api, errMsg } from "../api";

export default function Login() {
  const { login } = useAuth();
  const [mode, setMode] = useState("login");      // login | forgot
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  // forgot state
  const [step, setStep] = useState(1);            // 1: e-posta, 2: kod + yeni şifre
  const [fEmail, setFEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPass, setNewPass] = useState("");
  const [devCode, setDevCode] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try { await login(form.email, form.password); }
    catch (err) { setError(errMsg(err, "Giriş yapılamadı")); }
    finally { setBusy(false); }
  };

  const requestCode = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email: fEmail.trim() });
      toast.success(data.message || "Sıfırlama kodu oluşturuldu");
      if (data.dev_code) setDevCode(data.dev_code);   // GEÇİCİ: e-posta eklenince kalkacak
      setStep(2);
    } catch (err) { setError(errMsg(err, "Kod oluşturulamadı")); }
    finally { setBusy(false); }
  };

  const doReset = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      const { data } = await api.post("/auth/reset-password", { email: fEmail.trim(), code: code.trim(), new_password: newPass });
      toast.success(data.message || "Şifre güncellendi");
      setMode("login"); setStep(1); setCode(""); setNewPass(""); setDevCode("");
      setForm({ email: fEmail.trim(), password: "" });
    } catch (err) { setError(errMsg(err, "Şifre sıfırlanamadı")); }
    finally { setBusy(false); }
  };

  const backToLogin = () => { setMode("login"); setStep(1); setError(""); setCode(""); setNewPass(""); setDevCode(""); };

  return (
    <div className="login-shell" data-testid="login-page">
      <aside className="login-brand" style={{ "--login-watermark": "url(/brand/reen-mark-light.png)" }}>
        <div className="brand"><img className="brand-mark" src="/brand/reen-mark-light.png" alt="REEN Energy" /><div><b>REEN ENERGY</b><small>PROJE & FİNANS KONTROL</small></div></div>
        <div className="login-tagline">
          <img className="login-logo" src="/brand/reen-mark-light.png" alt="" />
          <span className="section-kicker">REEN ENERJİ</span>
          <h1>“Enerji Santrali<br />Geliştirme Merkezi”</h1>
          <p>İş kalemleri, alt yüklenici ödemeleri, kâr/zarar ve nakit akışı — proje ve işveren bazında, anlık.</p>
        </div>
        <small className="login-foot">© REEN Enerji</small>
      </aside>
      <main className="login-main">
        {mode === "login" && (
          <form className="login-card" onSubmit={submit} data-testid="login-form">
            <div className="login-icon"><LockKeyhole size={20} /></div>
            <span className="eyebrow">GÜVENLİ GİRİŞ</span>
            <h2>Hesabınıza giriş yapın</h2>
            <label>E-posta
              <input type="email" required autoFocus value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="ornek@sirket.com" data-testid="login-email-input" />
            </label>
            <label>Şifre
              <input type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder="••••••••" data-testid="login-password-input" />
            </label>
            {error && <div className="form-error" data-testid="login-error">{error}</div>}
            <button className="new-btn full" disabled={busy} data-testid="login-submit-button">
              <LogIn size={16} /> {busy ? "Giriş yapılıyor…" : "Giriş yap"}
            </button>
            <button type="button" className="link-btn" onClick={() => { setMode("forgot"); setFEmail(form.email); setError(""); }} data-testid="forgot-password-link">Şifremi unuttum?</button>
            <p className="login-hint">Hesabınız yoksa yöneticinizden kullanıcı oluşturmasını isteyin.</p>
          </form>
        )}

        {mode === "forgot" && step === 1 && (
          <form className="login-card" onSubmit={requestCode} data-testid="forgot-step1-form">
            <div className="login-icon"><KeyRound size={20} /></div>
            <span className="eyebrow">ŞİFRE SIFIRLAMA</span>
            <h2>Sıfırlama kodu al</h2>
            <label>E-posta
              <input type="email" required autoFocus value={fEmail} onChange={(e) => setFEmail(e.target.value)}
                placeholder="ornek@sirket.com" data-testid="forgot-email-input" />
            </label>
            {error && <div className="form-error" data-testid="forgot-error">{error}</div>}
            <button className="new-btn full" disabled={busy} data-testid="forgot-request-button">
              <KeyRound size={16} /> {busy ? "Gönderiliyor…" : "Kod oluştur"}
            </button>
            <button type="button" className="link-btn" onClick={backToLogin} data-testid="forgot-back-link"><ArrowLeft size={13} /> Girişe dön</button>
          </form>
        )}

        {mode === "forgot" && step === 2 && (
          <form className="login-card" onSubmit={doReset} data-testid="forgot-step2-form">
            <div className="login-icon"><KeyRound size={20} /></div>
            <span className="eyebrow">ŞİFRE SIFIRLAMA</span>
            <h2>Kodu girin ve şifreyi yenileyin</h2>
            {devCode && <div className="form-info" data-testid="forgot-dev-code">Test kodu (e-posta entegrasyonu eklenene kadar): <b>{devCode}</b></div>}
            <label>6 haneli kod
              <input required autoFocus value={code} onChange={(e) => setCode(e.target.value)} inputMode="numeric" maxLength={6}
                placeholder="______" data-testid="reset-code-input" />
            </label>
            <label>Yeni şifre
              <input type="password" required minLength={6} value={newPass} onChange={(e) => setNewPass(e.target.value)}
                placeholder="En az 6 karakter" data-testid="reset-password-input" />
            </label>
            {error && <div className="form-error" data-testid="reset-error">{error}</div>}
            <button className="new-btn full" disabled={busy} data-testid="reset-submit-button">
              <LockKeyhole size={16} /> {busy ? "Güncelleniyor…" : "Şifreyi güncelle"}
            </button>
            <button type="button" className="link-btn" onClick={() => { setStep(1); setError(""); }} data-testid="reset-resend-link">Kod gelmedi mi? Yeni kod al</button>
            <button type="button" className="link-btn" onClick={backToLogin} data-testid="reset-back-link"><ArrowLeft size={13} /> Girişe dön</button>
          </form>
        )}
      </main>
    </div>
  );
}
